/**
 * Email Queue API module.
 *
 * Provides endpoints for managing the email queue system including
 * queue entries, stats, cancel/retry actions, and template previews.
 */
import api from './client';
import type { PaginatedResponse } from './client';

export interface EmailQueueEntry {
  id: number;
  recipient_email: string;
  /** Name of the recipient's company (the customer), if known. */
  recipient_company: string | null;
  subject: string;
  /** Full HTML body — only present on the detail endpoint (getEntry). */
  body_html?: string;
  body_text?: string;
  body_preview: string;
  status: 'pending' | 'sending' | 'sent' | 'failed' | 'cancelled' | 'skipped' | 'rate_limited';
  notification_type: string;
  priority: string;
  retry_count: number;
  max_retries: number;
  error_message: string;
  batch_id: string;
  created_at: string;
  sent_at: string | null;
  cancelled_at: string | null;
  /** Provider delivery tracking (from the SendGrid event webhook). */
  delivered_at: string | null;
  opened_at: string | null;
  bounced_at: string | null;
  bounce_reason: string | null;
}

export interface EmailQueueStats {
  pending: number;
  sending: number;
  sent_24h: number;
  failed: number;
  cancelled: number;
  skipped: number;
  rate_limited?: number;
}

export interface EmailTemplateType {
  value: string;
  label: string;
  category: string;
}

/** One notification type present in the queue, with how many entries use it. */
export interface EmailQueueTypeFacet {
  value: string;
  count: number;
}

export const emailQueueAPI = {
  /** Fetch paginated queue entries with optional filters. */
  getEntries: (params?: Record<string, string | number | boolean>) =>
    api.get<PaginatedResponse<EmailQueueEntry>>('/admin/email/email-queue/', { params }),

  /** Fetch a single queue entry by ID. */
  getEntry: (id: number) =>
    api.get<EmailQueueEntry>(`/admin/email/email-queue/${id}/`),

  /**
   * Fetch aggregate stats for the queue dashboard.
   *
   * @param params - Optional scope, e.g. `{ site }` to count only one
   *   supplier site's mail. Pass the same scope as the list so the status tab
   *   counts agree with the rows shown beneath them.
   */
  getStats: (params?: { site?: number | string }) =>
    api.get<EmailQueueStats>('/admin/email/email-queue/stats/', { params }),

  /**
   * The notification types the queue actually holds, most common first.
   *
   * Drives the Type filter from data rather than a hardcoded list. Scoped
   * like the stats call, so pass the same site/customer scope as the list.
   *
   * @param params - Optional scope, e.g. `{ site, company }`
   * @returns Each type present, with its entry count
   */
  getNotificationTypes: (params?: Record<string, string | number>) =>
    api.get<EmailQueueTypeFacet[]>('/admin/email/email-queue/notification-types/', { params }),

  /**
   * Download the filtered queue as an Excel workbook.
   *
   * Pass exactly the filters the list is showing — the server exports what
   * they select, not the whole queue.
   *
   * @param params - The active list filters (status, type, search, company, site)
   * @returns The .xlsx payload as a Blob
   */
  exportEntries: (params?: Record<string, string | number | boolean>) =>
    api.get('/admin/email/email-queue/export/', { params, responseType: 'blob' }),

  /** Cancel a single pending queue entry. */
  cancelEntry: (id: number) =>
    api.post(`/admin/email/email-queue/${id}/cancel/`),

  /** Cancel all entries in a batch. */
  cancelBatch: (batchId: string) =>
    api.post('/admin/email/email-queue/cancel-batch/', { batch_id: batchId }),

  /** Cancel all pending entries in the queue. */
  cancelAll: () =>
    api.post('/admin/email/email-queue/cancel-all/'),

  /** Retry a single failed queue entry. */
  retryEntry: (id: number) =>
    api.post(`/admin/email/email-queue/${id}/retry/`),

  /** Retry all failed entries in the queue. */
  retryAll: () =>
    api.post('/admin/email/email-queue/retry-all/'),

  /** Preview a branded email template by notification type. */
  previewTemplate: (type: string) =>
    api.get<{ html: string }>('/admin/email/email-templates/preview/', { params: { type } }),

  /** List all available notification template types. */
  listTemplateTypes: () =>
    api.get<Record<string, EmailTemplateType[]>>('/admin/email/email-templates/types/'),
};
